<template>
  <div class="reply-container">
    <div class="avatar-bubble" :style="{ background: bubbleColor }">
      {{ avatar }}
    </div>

    <div class="content-wrapper">
      <div class="message-header">
        <PageRepliesFeatured v-if="props.featured === 1" />
        <span class="username">Door {{ username }}</span>
        <span class="divider"> • </span>
        <span class="post-time">{{ formattedPostTime }}</span>
      </div>

      <div class="message-box" :class="{ 'is-featured': props.featured === 1 }">
        <div class="message-text">
          {{ decodeHtmlEntities(post_text) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IForumReply } from '~~/types/local_types';
import formatUtils from '~/utils/formatUtils';
import { getUserGradientColors } from '~/utils/avatar';

const props = defineProps<IForumReply>();
const { decodeHtmlEntities } = formatUtils();

// Compute the final color based on special status or avatar string
const bubbleColor = computed(() => {
  if (props.special === 1) {
    return 'var(--effect-gradient-brand)';
  }

  if (!props.username) {
    return 'transparent';
  }

  const { start, end } = getUserGradientColors(props.username as string, {
    spread: 0.05,
    dimness: 0.5,
    hueTint: 50,
    hueVariance: 35,
  });

  return `linear-gradient(-45deg, ${start}, ${end})`;
});

const formattedPostTime = computed(() => {
  if (!props.post_time) return '';

  // 1. Grab just the date part: "2026-06-30"
  const datePart = props.post_time.split(' ')[0];

  // 2. Split it into [year, month, day]
  const [year, month, day] = datePart.split('-');

  // Fallback check if the string format is unexpected
  if (!year || !month || !day) return props.post_time;

  // 3. Parse as integers to automatically strip leading zeros (e.g., "06" becomes 6)
  const cleanDay = parseInt(day, 10);
  const cleanMonth = parseInt(month, 10);

  return `${cleanDay}-${cleanMonth}-${year}`;
});
</script>

<style lang="css" scoped>
.reply-container {
  display: flex;
  align-items: flex-start;
  gap: var(--unit-12);

  @media (--lg-viewport) {
    gap: var(--unit-16);
  }
}

/* Avatar Base Styles */
.avatar-bubble {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--unit-48);
  aspect-ratio: 1/1;
  border-radius: 50%;
  flex-shrink: 0;
  user-select: none;
  padding-top: 1.5px;

  color: hsl(var(--color-neutral-white));
  text-align: center;
  font-family: var(--font-family-primary), sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: var(--unit-20);
  line-height: var(--unit-14);

  @media (--lg-viewport) {
    font-size: var(--unit-26);
    line-height: var(--unit-16);
    width: var(--unit-60);
  }
}

/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.message-header {
  @mixin label-md-semi-bold;
  padding-bottom: var(--unit-6);
}

.username {
  color: hsl(var(--color-primary-600));
}

.divider {
  color: hsl(var(--color-primary-400));
}

.post-time {
  color: hsl(var(--color-primary-600));
}

.message-box {
  position: relative;
  background-color: hsl(var(--color-neutral-white));
  border-radius: var(--unit-4);
  padding: var(--unit-12) var(--unit-18);

  @media (--lg-viewport) {
    padding: var(--unit-16) var(--unit-24);
  }
}

.message-box::before {
  content: '';
  position: absolute;
  top: var(--unit-6);
  left: calc(var(--unit-8) * -1);
  width: 0;
  height: 0;
  border-top: var(--unit-6) solid transparent;
  border-bottom: var(--unit-6) solid transparent;
  border-right: var(--unit-12) solid hsl(var(--color-neutral-white));

  @media (--lg-viewport) {
    top: var(--unit-12);
    left: calc(var(--unit-10) * -1);
  }
}

.message-box.is-featured {
  border: 1px solid #ace1cb;
  box-shadow: 0 0 4px 1px #b6fbde;
}

.message-box.is-featured::before {
  filter: drop-shadow(0 0 1px #ace1cb) drop-shadow(0 0 1px #ace1cb) drop-shadow(0 0 2px #b6fbde);
  /* Clip the glow flush with the box's own left edge so it doesn't bleed
     back over the box's border where the tail overlaps it. */
  clip-path: inset(-12px 2px -12px -12px);
}

.message-text {
  @mixin body-md;
  color: hsl(var(--color-primary-950));
}
</style>
